import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { applyDir, getLang, setLang, t } from "./i18n";
import { refreshDmUnread, refreshMe, setState, toast, useStore } from "./store";
import { connect, subscribe } from "./api";
import { wireCalls } from "./calls";
import { wireVoice } from "./voice";
import { CallBar } from "./callbar";
import { Avatar, Coins, Link, Spinner, Toasts, cx } from "./ui";
import { Landing } from "./pages/landing";
import { Forgot, Login, Register, Reset, Verify } from "./pages/auth";
import { Home } from "./pages/home";
import { GameLobby } from "./pages/gamelobby";
import { Room } from "./pages/room";
import { Profile } from "./pages/profile";
import { Friends } from "./pages/friends";
import { Store, Wallet } from "./pages/store";
import { Leaderboard } from "./pages/leaderboard";
import { Notifications } from "./pages/notifications";
import { Settings } from "./pages/settings";
import { Messages } from "./pages/messages";
import { Help, Privacy, Terms } from "./pages/static";

applyDir();

const PUBLIC = ["/login", "/register", "/forgot", "/reset", "/verify", "/help", "/privacy", "/terms"];

function route(path: string, signedIn: boolean): React.ReactNode {
  const p = path.split("?")[0];
  const seg = p.split("/").filter(Boolean);
  if (p === "/") return signedIn ? <Home /> : <Landing />;
  if (p === "/login") return <Login />;
  if (p === "/register") return <Register />;
  if (p === "/forgot") return <Forgot />;
  if (p === "/reset") return <Reset />;
  if (p === "/verify") return <Verify />;
  if (p === "/help") return <Help />;
  if (p === "/privacy") return <Privacy />;
  if (p === "/terms") return <Terms />;
  if (!signedIn) return <Login next={path} />;
  if (seg[0] === "games" && seg[1]) return <GameLobby gameId={seg[1]} />;
  if (seg[0] === "room" && seg[1]) return <Room roomId={seg[1]} key={seg[1]} />;
  if (seg[0] === "u" && seg[1]) return <Profile username={decodeURIComponent(seg[1])} key={seg[1]} />;
  if (seg[0] === "messages") return <Messages username={seg[1] ? decodeURIComponent(seg[1]) : undefined} key={seg[1] ?? "list"} />;
  if (p === "/friends") return <Friends />;
  if (p === "/store") return <Store />;
  if (p === "/wallet") return <Wallet />;
  if (p === "/leaderboard") return <Leaderboard />;
  if (p === "/notifications") return <Notifications />;
  if (p === "/settings") return <Settings />;
  return <Home />;
}

const NAV = [
  { to: "/", key: "home", icon: "M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" },
  { to: "/messages", key: "messages", icon: "M4 5h16v11H9l-5 4z", badge: "dm" },
  { to: "/friends", key: "friends", icon: "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm-7 10a7 7 0 0 1 14 0M17 11a3 3 0 1 0 0-6M22 21a5 5 0 0 0-4-5" },
  { to: "/store", key: "store", icon: "M4 8h16l-1 12H5zM8 8a4 4 0 0 1 8 0" },
  { to: "/leaderboard", key: "leaderboard", icon: "M4 20V10h4v10M10 20V4h4v16M16 20v-7h4v7" },
  { to: "/settings", key: "settings", icon: "M4 6h9m4 0h3M15 4v4M4 12h3m4 0h9M9 10v4M4 18h11m4 0h1M17 16v4" },
];

/** Shows connecting for a short while, then an explicit failure with a retry button. */
function ConnStatus() {
  const [slow, setSlow] = React.useState(false);
  React.useEffect(() => {
    const id = setTimeout(() => setSlow(true), 8000);
    return () => clearTimeout(id);
  }, []);
  if (!slow) return <span className="conn">{t.connecting}</span>;
  return (
    <button type="button" className="conn conn-failed" onClick={() => { setSlow(false); connect(); }}>
      {t.connFailed} · {t.retry}
    </button>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const me = useStore((s) => s.me)!;
  const balance = useStore((s) => s.balance);
  const unread = useStore((s) => s.unread);
  const dmUnread = useStore((s) => s.dmUnread);
  const path = useStore((s) => s.path);
  const ws = useStore((s) => s.ws);
  const inGame = path.startsWith("/room/");
  const active = (to: string) => (to === "/" ? path === "/" || path.startsWith("/games") : path.startsWith(to));
  return (
    <div className={cx("shell", inGame && "shell-game")}>
      <header className="topbar">
        <Link to="/" className="brand">
          <img src="/brand/samra-symbol.svg" alt="" width="32" height="32" />
          <span>{t.appName}</span>
        </Link>
        <div className="topbar-end">
          {ws !== "online" && <ConnStatus />}
          <Link to="/wallet" className="balance-pill">
            <Coins n={balance?.total ?? 0} />
            <span className="plus" aria-label={t.topUp}>+</span>
          </Link>
          <Link to="/notifications" className="icon-btn bell" >
            <svg viewBox="0 0 24 24" width="22" height="22" aria-label={t.notifications}><path d="M6 17V11a6 6 0 0 1 12 0v6l2 2H4zM10 21h4" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinejoin="round" /></svg>
            {unread > 0 && <span className="badge">{unread > 9 ? "9+" : unread}</span>}
          </Link>
          <Link to={`/u/${me.username}`} className="me-link">
            <Avatar name={me.displayName} url={me.avatarUrl} size={34} />
          </Link>
        </div>
      </header>
      <nav className="rail" aria-label="main">
        {NAV.map((n) => (
          <Link key={n.to} to={n.to} className={cx("rail-item", active(n.to) && "is-active")}>
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d={n.icon} stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
            <span>{(t as any)[n.key]}</span>
            {n.badge === "dm" && dmUnread > 0 && <span className="badge rail-badge">{dmUnread > 9 ? "9+" : dmUnread}</span>}
          </Link>
        ))}
        {me.role !== "user" && (
          <a className="rail-item rail-admin" href="/admin">
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" stroke="currentColor" strokeWidth="1.8" fill="none" /></svg>
            <span>{t.admin}</span>
          </a>
        )}
      </nav>
      <main className="main">{children}</main>
      <CallBar />
    </div>
  );
}

function App() {
  const booted = useStore((s) => s.booted);
  const me = useStore((s) => s.me);
  const path = useStore((s) => s.path);
  useEffect(() => {
    void refreshMe();
    wireCalls();
    wireVoice();
    return subscribe((m) => {
      if (m.t === "gift.received") {
        const g: any = m.gift;
        toast(`${t.giftReceived(g.from.display_name)} — ${getLang() === "ar" ? g.name_ar : g.name_en}`, "gift");
      }
    });
  }, []);
  useEffect(() => {
    if (me?.locale && me.locale !== getLang() && !localStorage.getItem("samra.lang")) {
      setLang(me.locale);
      setState({});
    }
  }, [me?.locale]);
  if (!booted) return <div className="boot"><Spinner /></div>;
  const page = route(path, !!me);
  const bare = !me || PUBLIC.includes(path.split("?")[0]);
  return (
    <>
      {bare ? <div className="bare">{page}</div> : <Shell>{page}</Shell>}
      <Toasts />
    </>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
